
/**
 * Utility for comprehensive debugging of the game state processing flow
 */

// Global debug level - changing this will affect all debugging
let isDebugMode = true;

// Debug levels
export enum DebugLevel {
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
  CRITICAL = "CRITICAL"
}

// Track state transitions for diagnosis
interface StateSnapshot {
  timestamp: number;
  location: string;
  event: string;
  gameId: string | null;
  roundNumber: number | null;
  userWord: string | null;
  aiWord: string | null;
  userHasSubmitted: boolean | null;
  botHasChosenWord: boolean | null;
  botIsThinking: boolean | null;
}

const stateHistory: StateSnapshot[] = [];

/**
 * Enable or disable debug mode
 */
export const setDebugMode = (enabled: boolean) => {
  isDebugMode = enabled;
  if (enabled) {
    console.log("🔍 Debug mode ENABLED");
  } else {
    console.log("🔍 Debug mode DISABLED");
  }
};

/**
 * Log a debug message with full context
 */
export const debugLog = (
  level: DebugLevel,
  location: string,
  message: string,
  data?: any
) => {
  if (!isDebugMode) return;

  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level}] [${location}]`;
  
  if (data) {
    console.log(`${prefix} ${message}`, data);
  } else {
    console.log(`${prefix} ${message}`);
  }
};

/**
 * Take a snapshot of the current game state for debugging
 */
export const captureStateSnapshot = (
  location: string,
  event: string,
  gameId: string | null,
  roundNumber: number | null,
  userWord: string | null,
  aiWord: string | null,
  userHasSubmitted: boolean | null,
  botHasChosenWord: boolean | null, 
  botIsThinking: boolean | null
) => {
  if (!isDebugMode) return;

  const snapshot: StateSnapshot = {
    timestamp: Date.now(),
    location,
    event,
    gameId,
    roundNumber,
    userWord,
    aiWord,
    userHasSubmitted,
    botHasChosenWord,
    botIsThinking
  };

  stateHistory.push(snapshot);
  
  // Keep only the last 100 snapshots to avoid memory issues
  if (stateHistory.length > 100) {
    stateHistory.shift();
  }

  debugLog(
    DebugLevel.INFO, 
    location, 
    `STATE SNAPSHOT - ${event}`, 
    {
      roundNumber,
      userWord,
      aiWord,
      userHasSubmitted,
      botHasChosenWord,
      botIsThinking
    }
  );
};

/**
 * Verify the database round creation and state
 * This function can be called at critical points to ensure database consistency
 */
export const verifyRoundState = async (
  supabase: any,
  gameId: string,
  expectedRoundNumber: number
) => {
  if (!isDebugMode) return { exists: false, data: null };

  try {
    // Check if this round exists
    const { data, error } = await supabase
      .from("rounds")
      .select("*")
      .eq("game_id", gameId)
      .eq("round_number", expectedRoundNumber)
      .maybeSingle();
    
    if (error) {
      debugLog(DebugLevel.ERROR, "verification", `Error verifying round ${expectedRoundNumber}`, error);
      return { exists: false, data: null, error };
    }
    
    if (!data) {
      debugLog(DebugLevel.WARN, "verification", `Round ${expectedRoundNumber} does not exist in database`);
      return { exists: false, data: null, error: null };
    }
    
    debugLog(DebugLevel.INFO, "verification", `Round ${expectedRoundNumber} exists in database:`, data);
    return { exists: true, data, error: null };
  } catch (e) {
    debugLog(DebugLevel.ERROR, "verification", `Exception verifying round ${expectedRoundNumber}`, e);
    return { exists: false, data: null, error: e };
  }
};

/**
 * Dump the full state history to console for analysis
 */
export const dumpStateHistory = () => {
  if (!isDebugMode || stateHistory.length === 0) return;
  
  console.log("🔍 STATE HISTORY DUMP (most recent first):");
  console.table(stateHistory.slice().reverse());
};

/**
 * Print a summary of current round state across database and React state
 * This is useful for diagnosing synchronization issues
 */
export const summarizeGameState = async (
  supabase: any,
  gameId: string,
  currentRoundNumber: number,
  botIsThinking: boolean,
  botHasChosenWord: boolean,
  userHasSubmitted: boolean,
  processingComplete: boolean
) => {
  if (!isDebugMode) return;
  
  try {
    // Get database state for all rounds
    const { data: dbRounds, error } = await supabase
      .from("rounds")
      .select("*")
      .eq("game_id", gameId)
      .order("round_number", { ascending: true });
      
    if (error) {
      debugLog(DebugLevel.ERROR, "summary", "Error fetching rounds for summary", error);
      return;
    }
    
    debugLog(DebugLevel.INFO, "summary", "====== GAME STATE SUMMARY ======");
    debugLog(DebugLevel.INFO, "summary", `Game ID: ${gameId}`);
    debugLog(DebugLevel.INFO, "summary", `Current Round: ${currentRoundNumber}`);
    debugLog(DebugLevel.INFO, "summary", `React State: botIsThinking=${botIsThinking}, botHasChosenWord=${botHasChosenWord}, userHasSubmitted=${userHasSubmitted}, processingComplete=${processingComplete}`);
    debugLog(DebugLevel.INFO, "summary", `Database Rounds: ${dbRounds.length} rounds found`);
    
    // Check if the expected rounds exist in the database
    dbRounds.forEach(round => {
      debugLog(DebugLevel.INFO, "summary", `DB Round ${round.round_number}: user_word=${round.user_word || 'null'}, ai_word=${round.ai_word || 'null'}`);
    });
    
    // Check for any missing rounds
    for (let i = 1; i <= currentRoundNumber + 1; i++) {
      const found = dbRounds.some(r => r.round_number === i);
      if (!found) {
        debugLog(DebugLevel.WARN, "summary", `MISSING: Round ${i} is not in the database!`);
      }
    }
    
    debugLog(DebugLevel.INFO, "summary", "================================");
  } catch (e) {
    debugLog(DebugLevel.ERROR, "summary", "Error generating game state summary", e);
  }
};

/**
 * Add this to Window for easy access from browser console
 */
if (typeof window !== 'undefined') {
  (window as any).gameDebug = {
    setDebugMode,
    dumpStateHistory,
    getStateHistory: () => stateHistory.slice().reverse()
  };

  console.log("🔍 Game debug utilities available. Access via window.gameDebug in browser console");
  console.log("🔍 Try window.gameDebug.dumpStateHistory() to see state transitions");
}

// Export a simple version to be accessible in the browser console
export const gameDebug = {
  setDebugMode,
  dumpStateHistory,
  getStateHistory: () => stateHistory.slice().reverse()
};
